<template>
    <v-app-bar
        class="order-app-bar no-transition"
        :color="trainingMode ? '#ff9e0d' : '#1565c0'"
        app
        dark
        fixed
        prominent
        dense
    >
        <div v-if="loaded" class="wfull">
            <div class="d-flex align-center">
                <v-btn
                    icon
                    @click="eventBus.$emit('order-toggle-drawer')"
                >
                    <v-icon>far fa-bars</v-icon>
                </v-btn>

                <div v-if="curTable" class="table-label">
                    {{ $t('general.table') + ': ' + curTable.name }}
                </div>

                <v-spacer />

                <!-- <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            icon
                            color="white"
                            v-on="on"
                            @click="$tours['appTour'].start()"
                        >
                            <v-icon>fal fa-question-circle</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('tour.guide') }}</span>
                </v-tooltip> -->

                <div
                    v-if="!inventoryAccountingEnabled"
                    class="mx-2 d-flex align-center"
                >
                    <v-menu
                        transition="slide-y-transition"
                        bottom
                        right
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                :color="getPricelistColor()"
                                depressed
                                dark
                                rounded
                                :disabled="splitReceipt.active"
                                v-bind="attrs"
                                v-on="on"
                            >
                                {{ pricelist.name }} {{ $tc('general.pricelist') }}
                            </v-btn>
                        </template>

                        <v-list>
                            <v-list-item
                                v-for="item in pricelists"
                                :key="item.id"
                                @click="setPricelist(item.id)"
                            >
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>

                <connection-management-button />

                <!-- <div v-if="user" class="logged-user">
                    {{ user.name }}
                </div> -->

                <v-menu bottom transition="slide-y-transition">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" icon>
                            <v-icon>far fa-ellipsis-v</v-icon>
                        </v-btn>
                    </template>

                    <v-list v-if="user" class="user-menu">
                        <v-list-item :key="1" class="label">
                            <v-list-item-avatar>
                                <v-avatar class="primary" size="40">
                                    <v-icon dark>fal fa-user</v-icon>
                                </v-avatar>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title>{{ user.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ translateUserRole(user.role) }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>

                        <v-divider />

                        <v-list-item
                            v-if="allows('nav_access_admin')"
                            :key="2"
                            @click="$router.push({ name: 'adminDashboard' }).catch(err => {})"
                        >
                            <v-list-item-action class="minw25 ml-1">
                                <v-icon>fal fa-sliders-h</v-icon>
                            </v-list-item-action>
                            <v-list-item-title>{{ $t('general.administration') }}</v-list-item-title>
                        </v-list-item>

                        <v-divider />

                        <v-list-item :key="3" @click="clockOut">
                            <v-list-item-action class="minw25 ml-1">
                                <v-icon>fal fa-user-clock</v-icon>
                            </v-list-item-action>
                            <v-list-item-title>{{ $t('general.clock_out') }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>

            <div class="d-flex align-center">
                <v-tabs
                    :key="tabsKey"
                    v-model="currentType"
                    class="tabs"
                    background-color="transparent"
                    fixed-tabs
                    slider-color="yellow"
                >
                    <v-tab
                        v-for="(type, index) in productTypes"
                        :key="type"
                        :ref="`${type}Tab`"
                        :class="{ 'first-tab': index == 0 }"
                        :href="`#${type}`"
                    >
                        {{ $t(`general.${type}`) }}
                    </v-tab>
                </v-tabs>

                <v-btn
                    class="mr-2"
                    color="accent"
                    icon
                    small
                    @click="moveTabRight"
                >
                    <v-icon size="18">fal fa-chevron-right</v-icon>
                </v-btn>
            </div>
        </div>
    </v-app-bar>
</template>

<script>
import Pricelist from '@/models/Pricelist';
import UserService from '@/services/UserService';
import ConnectionManagementButton from '@/components/ConnectionManagementButton';
import { mapGetters } from 'vuex';
import * as mutationTypes from '@/store/mutationTypes';

const PRODUCT_TYPES_ORDER = 'products-type-order'

export default {
    name: 'OrderAppBar',

    components: {
        ConnectionManagementButton
    },

    async created() {
        const storedProductTypesOrder = await AppStorage.local().getItem(PRODUCT_TYPES_ORDER)
        if(storedProductTypesOrder) {
            this.productTypesOrder = storedProductTypesOrder.split(',')
        }

        this.productTypes = this.productTypesOrder.map(i => this.appConfig.product.types[i])
        this.currentType = this.productTypes[0]

        this.pricelists = [...this.pricelists, ...Pricelist.all()]

        setTimeout(() => this.setPricelist(), 100)

        this.loaded = true
        this.eventBus.$on('pricelist-update', this.onPricelistUpdate);
    },

    beforeDestroy() {
        this.eventBus.$off('pricelist-update', this.onPricelistUpdate);
    },

    data() {
        const pricelist = new Pricelist({
            id: 0,
            name: this.$t('general.pricelist_primary'),
            enabled: true
        })

        return {
            currentType: null,
            pricelists: [pricelist],
            pricelist: pricelist,
            productTypesOrder: [0, 1, 2],
            productTypes: [],
            tabsKey: 0,
            loaded: false
        };
    },

    watch: {
        currentType(val) {
            this.eventBus.$emit('order-show-tab', val)
        },

        curGuest(val) {
            setTimeout(() => this.setPricelist(), 100)
        }
    },

    computed: {
        ...mapGetters([
            'trainingMode',
            'curTable',
            'activePricelist',
            'splitReceipt',
            'curOrder',
            'unpaidOrders',
            'curGuest'
        ]),

        inventoryAccountingEnabled() {
            return !!this.tenant.venue?.settings?.use_inventory_accounting;
        }
    },

    methods: {
        translateUserRole(role) {
            // if default role and name translation exists use it
            const transKey = `user_roles.default.${role.name}`
            if(this.$t(transKey) !== transKey && role.id <= 4) {
                return this.$t(transKey)
            }

            return role.name
        },

        onPricelistUpdate(pricelistId) {
            const pricelistRemember = !!this.tenant?.venue?.settings?.pricelist_remember
            if(!pricelistRemember) {
                this.setPricelist(!pricelistId)
            }
        },

        clockOut() {
            UserService.clockOut();
            this.$router.push({ name: 'userLogin' }).catch(err => {});
        },

        getPricelistColor() {
            const pricelistColors = ['#2e70c5', '#2e3342', '#ffb213', '#00c2e8'];

            if(this.pricelist?.id === 0) return pricelistColors[0];
            if(this.pricelist?.name.toLowerCase().includes('vip')) return pricelistColors[1];
            if(this.pricelist?.name.toLowerCase().includes('glovo')) return pricelistColors[2];
            if(this.pricelist?.name.toLowerCase().includes('wolt')) return pricelistColors[3];

            const index = this.pricelists.findIndex(p => p.id === this.pricelist?.id)
            if(index === -1) return pricelistColors[0];

            return pricelistColors[index % 4];
        },

        moveTabRight() {
            const index = this.productTypes.indexOf(this.currentType)

            if (index >= this.productTypesOrder.length - 1) {
                let item = this.productTypesOrder.splice(index, 1)[0];
                this.productTypesOrder.unshift(item);
            } else if (index >= 0 && index < this.productTypesOrder.length - 1) {
                [this.productTypesOrder[index], this.productTypesOrder[index + 1]] = [this.productTypesOrder[index + 1], this.productTypesOrder[index]];
            }

            this.productTypes = this.productTypesOrder.map(i => this.appConfig.product.types[i])
            AppStorage.local().setItem(PRODUCT_TYPES_ORDER, this.productTypesOrder.join(','))
            this.tabsKey++
        },

        setPricelist(pricelistId = null) {
            if(pricelistId === null) {
                const pricelistRemember = !!this.tenant?.venue?.settings?.pricelist_remember
                const assignedPricelistId = this.unpaidOrders.length ? this.unpaidOrders[0].getPricelist() : 0

                pricelistId = pricelistRemember ? this.activePricelist : assignedPricelistId
            }

            this.pricelist = this.pricelists.find(p => p.id === pricelistId) || this.pricelists[0];
            this.$store.commit(mutationTypes.SET_ACTIVE_PRICELIST, { activePricelist: this.pricelist?.id || 0 });
        }
    }
};
</script>

<style lang="scss" scoped>
.order-app-bar {
    ::v-deep .v-toolbar__content {
        padding: 0;
    }

    .table-label {
        display: flex;
        align-items: center;
        font-size: 17px;
    }

    .select-pricelist {
        width: 120px;
    }

    .logged-user {
        margin-right: 8px;
        font-size: 15px;
        white-space: nowrap;
        text-align: right;
        display: flex;
        align-items: center;

        @media screen and (max-width: 500px) {
            display: none;
        }
    }

    .user-menu {
        padding: 0;
        font-size: 16px;

        .label {
            background: #f0f0f0;
        }
    }

    .tabs {
        .first-tab {
            margin-left: 0 !important;
        }

        ::v-deep .v-slide-group__prev {
            display: none !important;
        }
    }
}
</style>
